import * as Sentry from "@sentry/react"
import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, notFound, Outlet, redirect, useLocation } from "@tanstack/react-router"
import { type ErrorInfo, useEffect, useMemo } from "react"

import { ErrorPage } from "@/components/error/ErrorPage"
import { useErrorPageProps } from "@/components/error/useErrorPageProps"
import { Spinner } from "@/components/Spinner"
import { CameraProvider } from "@/helpers/camera/CameraProvider"
import { GeolocationProvider } from "@/helpers/GeolocationProvider"
import { getAddressOptions } from "@/hooks/api/useGetAddress"
import { getPhotosOptions } from "@/hooks/api/useGetPhotos"
import { getSessionConfigOptions } from "@/hooks/api/useGetSessionConfig"
import { getSessionDetailOptions } from "@/hooks/api/useGetSessionDetail"
import { prefetchSessionRoutes } from "@/routing/prefetchSessionRoutes"
import { SessionModelLoading } from "@/sections/session/SessionModelLoading"
import { SessionPermissionsChecker } from "@/sections/session/SessionPermissionsChecker"
import { loadOnnxModelOptions } from "@/services/model/model"
import { queryClient } from "@/services/queryClient"
import { BrowserSupport, resolveCurrentBrowserSupport } from "@/utils/browserSupport"
import { hasDismissedBrowserWarning } from "@/utils/browserWarningTracker"
import { registerFaroSession } from "@/utils/faro"
import { loadServerDataToDexie } from "@/utils/loadServerDataToDexie"
import { logger } from "@/utils/logging"
import { setTheme } from "@/utils/setTheme"
import { clearBlobUrlCache } from "@/utils/useBlobUrl"

import { SessionProvider } from "../../helpers/SessionProvider"
import { i18n, loadLocaleResources } from "../../i18n/i18n"

const unsupportedDevicePath = (sessionId: string) => `/${sessionId}/unsupportedDevice`

export const Route = createFileRoute("/$sessionId/_session")({
  component: SessionLayoutComponent,
  // Reports every error caught by this route's boundary to Sentry.
  // Wrapped in try/catch so a failure inside Sentry's reporting can never escalate into a hard
  // app crash (and is not silent) — e.g. captureReactException throwing on a malformed error.
  onCatch: (error: unknown, errorInfo?: ErrorInfo) => {
    try {
      Sentry.captureReactException(error, errorInfo ?? { componentStack: null })
    } catch (sentryError) {
      logger.error("Sentry captureReactException failed", sentryError)
    }
  },
  loader: async ({ params, location }) => {
    const { sessionId } = params

    // Sentry scope
    Sentry.setTag("sessionId", sessionId)

    const [sessionDetail, config, photos, address] = await Promise.all([
      queryClient.ensureQueryData({
        ...getSessionDetailOptions(sessionId),
      }),
      queryClient.ensureQueryData({
        ...getSessionConfigOptions(sessionId),
      }),
      queryClient.ensureQueryData({
        ...getPhotosOptions(sessionId),
      }),
      queryClient.ensureQueryData({
        ...getAddressOptions(sessionId),
      }),
    ])

    // Start ONNX model load in the background — don't block route render on it.
    // SessionModelLoading inside SessionLayoutComponent shows a wait screen until the
    // query finishes, so the user sees feedback while the model + wasm bytes download.
    queryClient.prefetchQuery({ ...loadOnnxModelOptions() })

    if (!sessionDetail || !config || !photos) {
      throw notFound()
    }

    // Correlates FE logs in Loki with server/worker logs through the same sessionId.
    registerFaroSession({
      sessionId,
      customerId: config.customerId,
      customerName: config.customerName,
      organizationId: config.organizationId,
    })

    // theme
    setTheme(config.theme)

    // locale
    await loadLocaleResources(config.language, config.organizationId)
    await i18n.changeLanguage(config.language)

    // Keep the declared document language in sync with the rendered content. The static index.html
    // ships lang="cs", so without this an English page looks like mistranslated Czech to the browser
    // and triggers auto-translation.
    document.documentElement.lang = config.language

    // session data
    await loadServerDataToDexie({ session: sessionDetail, photos, orderedSteps: config.orderedSteps, address })

    // Pre-fetch route chunks for all routes that user could reach during this session.
    // Chunks will be cached by service worker
    // Fire-and-forget: we don't block initial render on this.
    prefetchSessionRoutes(config.orderedSteps).catch(() => {
      logger.error("Failed to prefetch session routes")
    })

    // redirect if finished
    if (location.pathname !== `/${sessionId}/finished` && sessionDetail.finishedAt) {
      throw redirect({ to: "/$sessionId/finished", params: { sessionId } })
    }

    // blocks desktop devices and unwanted browsers, finished sessions are let through
    const browserSupport = resolveCurrentBrowserSupport()
    const isBlocked =
      browserSupport === BrowserSupport.DESKTOP ||
      (browserSupport === BrowserSupport.UNSUPPORTED_BROWSER && !hasDismissedBrowserWarning(sessionId))
    const isOnUnsupportedDeviceRoute = location.pathname === unsupportedDevicePath(sessionId)

    if (!sessionDetail.finishedAt && !isOnUnsupportedDeviceRoute && isBlocked) {
      logger.info(`Blocking session ${sessionId}: ${browserSupport}`)
      throw redirect({ to: "/$sessionId/unsupportedDevice", params: { sessionId }, replace: true })
    }

    return {
      theme: config.theme,
    }
  },
  pendingComponent: () => <Spinner position="center" />,
  errorComponent: ({ error }) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { sessionId } = Route.useParams()
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const errorPageProps = useErrorPageProps(error, sessionId)

    return <ErrorPage {...errorPageProps} sessionId={sessionId} error={error} />
  },
})

function SessionLayoutComponent() {
  const { theme: themeLoader } = Route.useLoaderData()
  const { sessionId } = Route.useParams()

  // Clear cached blob URLs when session changes to free up memory
  useEffect(() => clearBlobUrlCache, [sessionId])

  // This will mount global query observer, so when this query is invalidated, it will refetch in background
  // Data in loader fn are loaded only once, and then they may be re-fetch when router is invalidated
  const { data: sessionDetail } = useSuspenseQuery({ ...getSessionDetailOptions(sessionId) })
  if (!sessionDetail) {
    throw notFound()
  }

  const location = useLocation()

  // watchPosition (GeolocationProvider) is the single source of truth for geolocation — it
  // produces both the GPS position and the permission state. Its enablement therefore depends
  // ONLY on the route, not on permission state: gating it on "granted" would deadlock (nothing
  // would start the watch that produces "granted"), and it must also work without
  // navigator.permissions (SDK / old iOS), where the state comes purely from the watch callbacks.
  // Disabled only on intro/demo/index; on the guide it stays ON (granting is the point there, and
  // a fresh watch after the guide's reload is what re-detects a flipped OS setting).
  const geolocationEnabled = useMemo(() => {
    const isIndexRoute = location.pathname === `/${sessionId}` || location.pathname === `/${sessionId}/`
    const isDisabledRoute = location.pathname.includes("intro") || location.pathname.includes("demo") || isIndexRoute

    return !isDisabledRoute
  }, [location.pathname, sessionId])

  // display logo, but dont load onnx model, permissions checker & geolocation provider
  if (location.pathname === unsupportedDevicePath(sessionId)) {
    return (
      <SessionProvider session={sessionDetail} theme={themeLoader}>
        <Outlet />
      </SessionProvider>
    )
  }

  return (
    <GeolocationProvider enabled={geolocationEnabled}>
      <CameraProvider>
        <SessionProvider session={sessionDetail} theme={themeLoader}>
          <SessionPermissionsChecker sessionId={sessionId} />
          <SessionModelLoading>
            <Outlet />
          </SessionModelLoading>
        </SessionProvider>
      </CameraProvider>
    </GeolocationProvider>
  )
}
